import type { AccountEquipment } from '@/types/auth'

/**
 * NMS (Network Management System) type constants
 */
export const NMS_TYPES = {
	CALIX: 'calix',
	UISP: 'uisp',
	CAMBIUM: 'cambium'
} as const

/**
 * NMS (Network Management System) keyword mappings
 * Maps NMS identifiers to device name keywords
 */
const NMS_MAPPING = {
	[NMS_TYPES.CALIX]: ['calix'],
	[NMS_TYPES.UISP]: ['uisp', 'ubiquiti', 'unifi', 'edge router', 'edge switch'],
	[NMS_TYPES.CAMBIUM]: ['cambium']
} as const

export type NmsType = keyof typeof NMS_MAPPING | ''

/**
 * Detects the Network Management System (NMS) type for a given equipment
 *
 * @param equipment - The account equipment object
 * @returns The detected NMS type ('calix', 'uisp', 'cambium', or empty string if unknown)
 *
 * @example
 * const nms = detectDeviceNms({ deviceName: 'Calix 844G-1', networkController: 'calix' })
 * // Returns: 'calix'
 */
export const detectDeviceNms = (equipment: AccountEquipment): NmsType => {
	// First priority: Use explicit networkController if available
	if (equipment.networkController) {
		return equipment.networkController.toLowerCase() as NmsType
	}

	// Fallback: Parse deviceName to extract brand
	const deviceName = equipment.deviceName?.toLowerCase()
	if (!deviceName) {
		return ''
	}

	// Check device name against known NMS keywords
	for (const [nms, keywords] of Object.entries(NMS_MAPPING)) {
		if (keywords.some(keyword => deviceName.includes(keyword))) {
			return nms as NmsType
		}
	}

	// Unknown device
	return ''
}

/**
 * Checks if a device is supported by the device tools backend
 * Currently supported: Calix and UISP devices
 *
 * @param nms - The NMS type identifier
 * @returns True if the device is supported for device tools operations
 */
export const isDeviceSupportedForTools = (nms: string): boolean => {
	const normalizedNms = nms?.toLowerCase()
	return normalizedNms === NMS_TYPES.CALIX || normalizedNms === NMS_TYPES.UISP
}
